import { Entity, Index, OneToMany } from 'typeorm';
import { Column } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { InvoiceEntity } from './invoice.entity';

/**
 * How the invoice due date is derived from the invoice date.
 *
 * INVOICE_DATE   — dueDate = invoiceDate + dueOffsetDays (the classic
 *                  "Net 30" family).
 * END_OF_MONTH   — dueDate = endOfMonth(invoiceDate) + dueOffsetDays
 *                  (the "EOM + 10", "EOM + 30" family used heavily by
 *                  pharmaceutical tenants).
 * ON_RECEIPT     — dueDate = invoiceDate (covers Due-on-Receipt, COD,
 *                  Prepay; dueOffsetDays is ignored).
 */
export enum TermsDueOffsetType {
	INVOICE_DATE = 'invoice_date',
	END_OF_MONTH = 'end_of_month',
	ON_RECEIPT = 'on_receipt',
}

@Index('terms_code_code_idx', ['code'], { unique: true })
@Index('terms_code_sortOrder_idx', ['sortOrder'])
@Entity('terms_code')
export class TermsCodeEntity extends BaseEntity {
	/** Stable identifier, e.g. NET_30, EOM_10, ON_RECEIPT. Unique per tenant. */
	@Column('varchar', { name: 'code', length: 32 })
	public code!: string;

	/** Display label shown in dropdowns and on invoices, e.g. "Net 30". */
	@Column('varchar', { name: 'label', length: 100 })
	public label!: string;

	@Column('enum', {
		name: 'dueOffsetType',
		enum: TermsDueOffsetType,
		default: TermsDueOffsetType.INVOICE_DATE,
	})
	public dueOffsetType!: TermsDueOffsetType;

	/** Days added to the base date (ignored when dueOffsetType is ON_RECEIPT). */
	@Column('int', { name: 'dueOffsetDays', default: () => "'0'" })
	public dueOffsetDays!: number;

	/** Single-step early-pay discount percent. Null = no discount offered. */
	@Column('decimal', {
		name: 'earlyPayDiscountPercent',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public earlyPayDiscountPercent!: string | null;

	/** Days from invoice date the discount is valid. Null when no discount. */
	@Column('int', { name: 'discountWindowDays', nullable: true })
	public discountWindowDays!: number | null;

	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	@Column({ type: 'boolean', name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	/**
	 * Tenant-default flag. Application-level enforcement: toggling true
	 * on any row flips every other row's value to false in the same
	 * transaction. MySQL filtered-unique-index workarounds aren't worth
	 * the complexity here.
	 */
	@Column({ type: 'boolean', name: 'isDefault', width: 1, default: () => "'0'" })
	public isDefault!: boolean;

	@OneToMany(() => ClientEntity, (client) => client.fkTermsCode, {})
	public clients!: Array<ClientEntity>;

	@OneToMany(() => InvoiceEntity, (invoice) => invoice.fkTermsCode, {})
	public invoices!: Array<InvoiceEntity>;
}
